/**
 * Grand Quack Auto — multiplayer system.
 *
 * "Shared hallucination" pattern from research §4: sync only duck positions
 * at 10 Hz, render everything else locally. Other players' ducks are
 * interpolated 100ms in the past for smooth display.
 *
 * See research.md §4 "Multiplayer: the shared hallucination approach".
 */

import * as THREE from 'three';
import { Client } from 'colyseus.js';
import { MULTIPLAYER } from '../config';
import { createDuck } from '../entities/duck';
import type { System, World } from '../types';
import { lerp } from '../utils/mathUtils';

// ── Types ─────────────────────────────────────────────────────────────────

interface PositionSnapshot {
  x: number; y: number; z: number; ry: number;
  time: number;
}

interface RemoteDuck {
  group: THREE.Group;
  snapshots: PositionSnapshot[];
}

// ── Module state ──────────────────────────────────────────────────────────

let room: { send: (type: string, data: unknown) => void; sessionId: string; leave: () => void } | null = null;
let connected = false;
let connecting = false;
const remoteDucks = new Map<string, RemoteDuck>();
let lastSendTime = 0;
let onlineEl: HTMLElement | null = null;

// ── Connection ────────────────────────────────────────────────────────────

async function connect(world: World): Promise<void> {
  connecting = true;
  const serverUrl = import.meta.env.VITE_SERVER_URL as string | undefined
    ?? MULTIPLAYER.defaultServerUrl;

  try {
    const client = new Client(serverUrl);
    const name = `Duck${Math.floor(Math.random() * 999)}`;
    const joined = await client.joinOrCreate(MULTIPLAYER.roomName, { name });

    room = joined;
    connected = true;
    console.log(`[multiplayer] joined room ${joined.roomId} as "${name}"`);

    // Listen for other ducks joining — "Duck Drop" entrance.
    joined.state.ducks.onAdd((duckState: Record<string, unknown>, sessionId: string) => {
      if (sessionId === joined.sessionId) return; // skip self
      const duckName = duckState.name as string;
      const group = createRemoteDuck(world, duckName);
      remoteDucks.set(sessionId, { group, snapshots: [] });
      updateOnlineCount(joined.state.ducks.size);

      // Duck Drop: start high, fall to ground over 1s.
      group.position.y = 30;
      const dropStart = performance.now();
      const dropAnim = () => {
        const t = Math.min((performance.now() - dropStart) / 1000, 1);
        group.position.y = 30 * (1 - t * t); // quadratic ease-in
        if (t < 1) requestAnimationFrame(dropAnim);
      };
      requestAnimationFrame(dropAnim);

      // Entrance notification.
      const note = document.createElement('div');
      note.className = 'wanted-notification';
      note.textContent = `${duckName} has entered the pond.`;
      note.style.color = '#44ff44';
      document.getElementById('hud')?.appendChild(note);
      setTimeout(() => note.remove(), 3000);
    });

    // Listen for ducks leaving.
    joined.state.ducks.onRemove((_duckState: unknown, sessionId: string) => {
      const rd = remoteDucks.get(sessionId);
      if (rd) {
        world.scene.remove(rd.group);
        remoteDucks.delete(sessionId);
      }
      updateOnlineCount(joined.state.ducks.size);
    });

    // Listen for state changes on each duck.
    joined.state.ducks.onChange((duckState: Record<string, unknown>, sessionId: string) => {
      if (sessionId === joined.sessionId) return;
      const rd = remoteDucks.get(sessionId);
      if (!rd) return;
      rd.snapshots.push({
        x: duckState.x as number,
        y: duckState.y as number,
        z: duckState.z as number,
        ry: duckState.ry as number,
        time: performance.now(),
      });
      // Keep only last 5 snapshots.
      if (rd.snapshots.length > 5) rd.snapshots.shift();
    });

    updateOnlineCount(joined.state.ducks.size);
  } catch (e) {
    console.warn('[multiplayer] connection failed, running offline:', e);
    connecting = false;
  }
}

function createRemoteDuck(world: World, name: string): THREE.Group {
  // Reuse duck geometry but don't overwrite world.duck.
  const tempWorld = { ...world, duck: new THREE.Group() } as World;
  const group = createDuck(tempWorld);
  group.name = `remote-duck-${name}`;

  // Add floating name tag (billboard sprite).
  const canvas = document.createElement('canvas');
  canvas.width = 256; canvas.height = 48;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffd700';
  ctx.font = 'bold 24px Arial';
  ctx.textAlign = 'center';
  ctx.fillText(name, 128, 32);
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: new THREE.CanvasTexture(canvas), transparent: true }),
  );
  sprite.position.y = 1.8;
  sprite.scale.set(2, 0.4, 1);
  group.add(sprite);

  return group;
}

function updateOnlineCount(count: number): void {
  if (!onlineEl) onlineEl = document.getElementById('hud-online');
  if (onlineEl) onlineEl.textContent = `${count} duck${count !== 1 ? 's' : ''} online`;
}

// ── System ────────────────────────────────────────────────────────────────

export const multiplayerSystem: System = {
  name: 'multiplayer',

  fixedUpdate(_dt: number, world: World) {
    // Attempt connection on first tick.
    if (!connected && !connecting) {
      connect(world);
      return;
    }
    if (!room || !connected) return;

    // Send position at syncHz (10 Hz).
    const now = performance.now();
    const interval = 1000 / MULTIPLAYER.syncHz;
    if (now - lastSendTime < interval) return;
    lastSendTime = now;

    const duck = world.duck;
    const isMoving = Math.hypot(world.input.move.x, world.input.move.y) > 0.01;
    room.send('pos', {
      x: duck.position.x,
      y: duck.position.y,
      z: duck.position.z,
      ry: duck.rotation.y,
      anim: isMoving ? 'walk' : 'idle',
    });
  },

  render(_dt: number, _alpha: number, _world: World) {
    // Interpolate remote ducks — display 100ms in the past.
    const displayTime = performance.now() - MULTIPLAYER.interpBufferMs;

    for (const rd of remoteDucks.values()) {
      const snaps = rd.snapshots;
      if (snaps.length < 2) {
        // Not enough data to interpolate — snap to latest.
        if (snaps.length === 1) {
          rd.group.position.set(snaps[0]!.x, snaps[0]!.y, snaps[0]!.z);
          rd.group.rotation.y = snaps[0]!.ry;
        }
        continue;
      }

      // Find the two snapshots bracketing displayTime.
      let prev = snaps[0]!;
      let next = snaps[1]!;
      for (let i = 1; i < snaps.length; i++) {
        if (snaps[i]!.time > displayTime) {
          next = snaps[i]!;
          prev = snaps[i - 1]!;
          break;
        }
        prev = snaps[i]!;
        next = snaps[Math.min(i + 1, snaps.length - 1)]!;
      }

      // Lerp between prev and next.
      const range = next.time - prev.time;
      const t = range > 0 ? Math.min(1, (displayTime - prev.time) / range) : 1;
      rd.group.position.set(
        lerp(prev.x, next.x, t),
        lerp(prev.y, next.y, t),
        lerp(prev.z, next.z, t),
      );
      rd.group.rotation.y = lerp(prev.ry, next.ry, t);
    }
  },
};
